/**
 * arretesService — wrapper rétro-compatible au-dessus de `clausierService`.
 *
 * Avant : 2 appels HTTP dédiés (/articles-arretes + /templates-observations-arretes)
 * et stockage IndexedDB séparé.
 * Maintenant : on lit la sous-section `arretes` de l'arbre clausier
 * (qui ne demande qu'1 seul appel agrégé, partagé avec les normes).
 *
 * L'API publique (`list()`, `refresh()`, `clearCache()`, `getCacheStats()`)
 * reste identique pour ne rien casser dans `useArretesTemplates`.
 */

import clausierService, { buildArretesIndex } from './clausierService';

/**
 * Configuration statique des arrêtés (alignée avec l'app principale et le serveur).
 * Conservée ici uniquement pour rester un point d'import existant si besoin :
 * la source officielle reste celle du serveur (renvoyée dans tree.arretes).
 */
export const ARRETES_CONFIG = [
  { id: 'arrete_14_12_2011', code: '14/12/2011', titre: 'Arrêté du 14/12/2011', description: "Installations d'éclairage de sécurité" },
  { id: 'arrete_15_12_2011', code: '15/12/2011', titre: 'Arrêté du 15/12/2011', description: 'Galvanoplastie et électrophorèse' },
  { id: 'arrete_16_12_2011', code: '16/12/2011', titre: 'Arrêté du 16/12/2011', description: "Laboratoires et plateformes d'essais" },
  { id: 'arrete_19_12_2011', code: '19/12/2011', titre: 'Arrêté du 19/12/2011', description: 'Soudage électrique' },
  { id: 'arrete_20_12_2011', code: '20/12/2011', titre: 'Arrêté du 20/12/2011', description: 'Appareils électriques amovibles' },
  { id: 'arrete_23_12_2011', code: '23/12/2011', titre: 'Arrêté du 23/12/2011', description: 'Équipements de travail' },
  { id: 'arrete_28_07_2003', code: '28/07/2003', titre: 'Arrêté du 28/07/2003', description: 'Atmosphères explosives (ATEX)' },
];

/** Normalise un code arrêté (`19/12/2011` → `19_12_2011`) pour construire un lien étape 14 stable. */
export const buildArreteEtape14Link = (arreteCode, articleNumero) => {
  if (!arreteCode || !articleNumero) return '';
  return `arr_${String(arreteCode).replace(/\//g, '_')}_art${articleNumero}`;
};

class ArretesService {
  /**
   * Retourne { articles, templates, byArreteCode, byArticleId } construits à partir
   * de l'arbre clausier (cache 24h + ETag, partagé avec `referentielService`).
   */
  async list() {
    const tree = await clausierService.getTree();
    return buildArretesIndex(tree);
  }

  async refresh() {
    const tree = await clausierService.refresh();
    return buildArretesIndex(tree);
  }

  /**
   * Cache partagé avec clausierService → clearCache() effectif via clausierService.
   * Conservé ici pour rétro-compatibilité avec `RefreshReferentielButton`.
   */
  async clearCache() {
    await clausierService.clearCache();
  }

  async getCacheStats() {
    return clausierService.getCacheStats();
  }
}

const arretesService = new ArretesService();
export default arretesService;
